/**
 * Exhaust.
 *
 * A ship under power looked exactly like a ship coasting: the thrusters are
 * modelled, lit and pointed the right way, and nothing came out of them. This is
 * the one visual cue that says a hull is *working* rather than drifting, and it
 * costs a single draw call.
 *
 * ── The frame is the whole of it ────────────────────────────────────────────
 *
 * **Everything here is in absolute coordinates**, like `shipAt` and like
 * `wreckage`. Not scene space. That is not a detail — it is the effect.
 *
 * The flight view rebases the scene onto the ship *every frame*
 * (`origin.copy(shipAt)`), so a scene-space position is a *ship-space* position
 * wearing a different name. The first version of this stored particles in scene
 * space and then added the ship's velocity to them, which is wrong twice over: a
 * particle was pinned to the hull no matter what velocity it was given, and the
 * velocity it was given pushed it *forwards*, so at speed the exhaust raced out
 * ahead of the ship it was supposed to be trailing behind.
 *
 * Storing absolutes and subtracting `origin` at draw time fixes both at once,
 * and gives the effect for free: a particle that barely moves in world space
 * streaks backwards past the camera at exactly the speed the ship is doing,
 * because that is what is actually happening. **Nothing here needs to know how
 * fast the ship is going.** The apparent speed of the plume is the ship leaving.
 *
 * ── Quads, not lines ────────────────────────────────────────────────────────
 *
 * A GL line is one pixel on nearly every platform — `linewidth` is ignored — so
 * the previous version faked thickness with *more* streaks and still read as
 * hair at any distance. These are camera-facing quads: a real width that holds
 * up close, and a real length that does not change as the particle ages.
 *
 * One instanced draw call. The base quad is four vertices; everything that
 * differs per particle is an instanced attribute, and only two of those
 * (`iCentre`, `iAlpha`) are rewritten each frame.
 *
 * ── The log-depth chunks are not optional ───────────────────────────────────
 *
 * Flight runs `logarithmicDepthBuffer: true`, and three only injects the chunks
 * that implement it into *its own* materials. A custom shader without them
 * writes ordinary depth into a buffer everything else filled logarithmically, so
 * it loses every comparison and the plume draws behind the entire scene —
 * visible, and under everything, which reads as a sorting bug rather than as a
 * missing include. Same trap and same fix as `shaders/sun.ts`.
 */

import * as THREE from 'three';

/**
 * How long a particle lasts, in seconds.
 *
 * Short, and this is the constant that sets how long the trail is — because
 * particles sit still and the ship leaves them, a plume's length is *lifetime ×
 * ship speed*. At cruise (120u/s) this leaves about 75 units of trail and at
 * warp (800u/s) about 500, so the trail visibly lengthens with speed, which is
 * the point, while staying a plume rather than a contrail drawn across the
 * system.
 */
const LIFETIME = 0.62;

/**
 * The most particles alive at once, across every thruster.
 *
 * A fixed pool. Nothing is allocated after construction and nothing is freed
 * until the scene goes — a plume that garbage-collects mid-burn would stutter
 * exactly when the player is doing the thing that emits it.
 *
 * A hull with more thrusters than this can feed overwrites its own oldest
 * particles, which shortens its trail rather than dropping it. That is the right
 * way round: the failure is a plume that is too short, not a plume that flickers.
 */
const POOL = 1024;

/**
 * How fast exhaust leaves the nozzle, in world units per second.
 *
 * **Very small, on purpose.** This is not what makes the plume move — the ship
 * leaving it behind is. All this does is push the particle clear of the nozzle
 * so a stationary ship still has something visible coming out of it, and give
 * the cone a little life. Raise it much and the exhaust starts outrunning the
 * hull, which is what made the first version look like sparks off a grinder.
 */
const EXHAUST_SPEED = 2.2;

/**
 * What the exhaust does instead, while crossing between systems.
 *
 * ── The one place the whole model breaks down ───────────────────────────────
 *
 * Everything above rests on the ship moving and the plume being left behind. In
 * the tunnel the ship is not in the system at all: the flight model stops, the
 * velocity is zero, and a plume that gets its motion from being overtaken gets
 * none. Emitting anyway just piles a stationary puff on the nozzle.
 *
 * So for the crossing, and only for the crossing, the relationship is inverted —
 * the exhaust moves and the ship does not. It is the same picture from the other
 * side, and since the crossing is the one stretch of the game with no true
 * position to be honest about, there is nothing to be dishonest with.
 *
 * Sized so the trail is a hundred-odd units at `LIFETIME`, which is a hull length
 * or two: a hard burn, without the plume outrunning the tunnel around it.
 */
export const TUNNEL_EXHAUST = 170;

/** Sideways scatter, so a plume opens into a cone rather than a column. */
const SPREAD = 1.1;

/**
 * How far back from the component's own origin the exhaust starts.
 *
 * **This is the one to change if the plume does not sit in the cone.** A
 * thruster's placement box is deliberately not its visible size — the hitbox is
 * what it occupies on the grid, the model is what you see — so the component's
 * origin is somewhere inside the part rather than at the mouth of its nozzle.
 * There is no way to derive the difference; it is a fact about the model.
 *
 * Measured in hull units, backwards along the exhaust. Raise it to push the
 * plume further out of the ship, lower it to pull the plume into the hull.
 */
export const NOZZLE_DEPTH = 2.5;

/**
 * How wide the mouth of the plume is.
 *
 * Particles start scattered across a disc this wide rather than all at one
 * point, which is what gives a plume body at the nozzle instead of a thread that
 * only opens out further down.
 */
const MOUTH = 0.5;

/** How long a streak is drawn, in world units. Fixed at birth, never changes. */
const STREAK_LENGTH = 3;

/** How wide a streak is drawn, in world units. Fixed at birth, never changes. */
const STREAK_WIDTH = 0.44;

/**
 * How much streak lengths vary between particles, as a fraction.
 *
 * Variation *between* particles, never *within* one: a streak is the length it
 * was born and stays it. A plume of identically-sized quads reads as a
 * repeating texture, which is the one thing a particle system must not do.
 */
const LENGTH_JITTER = 0.45;

/**
 * How far a streak may be rolled off the exhaust direction, in radians.
 *
 * A plume of streaks all lying along one axis is a comb: every quad is parallel
 * to its neighbour, so the gaps between them line up and the plume looks like
 * stripes however many are in it. Rolling each one by its own angle crosses them
 * over, and the overlaps fill the cone without a single quad being any wider —
 * which is the point, because widening them is what turns a streak into a smear.
 *
 * `Math.PI` is a free roll. Reduce it for a fanned look where the streaks still
 * broadly point the way the exhaust is going.
 */
const ROLL_SPREAD = Math.PI;

/** Particles a second, per nozzle, at full throttle. */
const EMIT_RATE = 120;

/**
 * What a plume is burning, as a pair of colours.
 *
 * `hot` and `cool` are the ends of a gradient, and every particle picks **one
 * flat colour from a random point along it** at birth and keeps it. Not a
 * gradient drawn along each streak: a plume is a spray of embers at different
 * temperatures, not a row of identical ones that each fade the same way, and
 * mixing colours *between* particles is what makes it look like a spray.
 *
 * Naming them by temperature rather than by position is what keeps them the
 * right way round when the palettes are edited.
 */
export interface PlumeColors {
  hot: number;
  cool: number;
}

/** Ordinary thrust: light orange, falling to red. */
export const THRUST_FLAME: PlumeColors = { hot: 0xffb46b, cool: 0xd6342a };

/**
 * The warp drive, spun up but still in the system.
 *
 * White to blue. Deliberately the opposite end of the spectrum from combustion —
 * a drive doing something a chemical rocket cannot should not look like one.
 */
export const WARP_FLAME: PlumeColors = { hot: 0xffffff, cool: 0x3f7bff };

/**
 * Inside the tunnel.
 *
 * Blue to violet, matching the crossing itself. The ship is not in the system
 * any more, and the exhaust says so before anything else on screen does.
 */
export const TUNNEL_FLAME: PlumeColors = { hot: 0x6fa8ff, cool: 0xb46bff };

export interface ThrusterPlume {
  /**
   * What the plume is burning, from here on.
   *
   * Affects particles emitted *after* it, never ones already in the air —
   * colour is baked in at birth. That is correct rather than a limitation: a
   * particle already out of the nozzle burned what it burned, so spinning up the
   * warp drive changes the plume from the nozzle outwards instead of recolouring
   * the whole trail at once.
   */
  setColors: (colors: PlumeColors) => void;
  /**
   * Emit from a nozzle, this frame.
   *
   * `at` is **absolute**, not scene space — the caller adds the render origin
   * back on. See the note at the top of this file; getting it wrong is silent
   * and looks like the plume being welded to the ship.
   *
   * `back` is the unit direction the exhaust leaves along, and doubles as the
   * streak's long axis. `drift` is the ship's own velocity, used *only* to
   * spread this frame's particles along the path the nozzle actually swept —
   * never added to a particle's own motion.
   *
   * `strength` is 0..1 and scales how many particles appear rather than how fast
   * they go: a gentle burn is a thinner plume, not a slower one.
   */
  emit: (
    at: THREE.Vector3,
    back: THREE.Vector3,
    drift: THREE.Vector3,
    strength: number,
    seconds: number,
    /**
     * How big this nozzle's plume is, relative to a main thruster.
     *
     * A small thruster is a smaller engine and should look like one — this
     * scales the streak, the mouth and the spread together, so its plume is
     * shorter and tighter rather than merely thinner.
     */
    scale?: number,
    /**
     * How fast the exhaust leaves, overriding the default.
     *
     * Only the crossing has any business setting this — see `TUNNEL_EXHAUST`.
     * Everywhere else the plume's motion is the ship's, and turning this up is
     * how the first version came to look like sparks off a grinder.
     */
    exhaust?: number,
  ) => void;
  /**
   * Age everything and redraw. Called once a frame, after emitting.
   *
   * `origin` is the scene's rebase, and this is the only place it is applied:
   * absolutes go in, scene-space vertices come out.
   */
  step: (seconds: number, origin: THREE.Vector3) => void;
  dispose: () => void;
}

/**
 * A plume, drawn into a scene.
 *
 * One instanced mesh for the whole pool. Per-frame work is two buffer uploads
 * and no scene-graph traffic at all — a thousand `Object3D`s would be a thousand
 * matrix updates a frame for something that is never a parent of anything.
 */
export function createThrusterPlume(
  scene: THREE.Scene,
  colors: PlumeColors = THRUST_FLAME,
): ThrusterPlume {
  const geometry = new THREE.InstancedBufferGeometry();

  /*
   * The quad, in corner coordinates rather than in space.
   *
   * `x` runs along the streak and `y` across it, both −1..1, and the vertex
   * shader turns that into a position. The quad has no size of its own; every
   * particle supplies its own in `iSize`.
   */
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(
      new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]),
      3,
    ),
  );
  geometry.setIndex([0, 1, 2, 0, 2, 3]);

  /** Where the streak is, this frame. Absolute minus origin. Rewritten always. */
  const centres = new Float32Array(POOL * 3);
  /** Which way it lies. Written at birth; a streak does not turn as it ages. */
  const axes = new Float32Array(POOL * 3);
  /** Half-length and half-width. Written at birth — this is rule four. */
  const sizes = new Float32Array(POOL * 2);
  /** Its one flat colour, sampled from the gradient at birth. */
  const tints = new Float32Array(POOL * 3);
  /** How far it is rolled on screen. Written at birth; a quad does not spin. */
  const rolls = new Float32Array(POOL);
  /** How bright it still is. The only thing about a particle that changes. */
  const alphas = new Float32Array(POOL);

  const iCentre = new THREE.InstancedBufferAttribute(centres, 3);
  const iAxis = new THREE.InstancedBufferAttribute(axes, 3);
  const iSize = new THREE.InstancedBufferAttribute(sizes, 2);
  const iColor = new THREE.InstancedBufferAttribute(tints, 3);
  const iRoll = new THREE.InstancedBufferAttribute(rolls, 1);
  const iAlpha = new THREE.InstancedBufferAttribute(alphas, 1);

  iCentre.setUsage(THREE.DynamicDrawUsage);
  iAlpha.setUsage(THREE.DynamicDrawUsage);

  geometry.setAttribute('iCentre', iCentre);
  geometry.setAttribute('iAxis', iAxis);
  geometry.setAttribute('iSize', iSize);
  geometry.setAttribute('iColor', iColor);
  geometry.setAttribute('iRoll', iRoll);
  geometry.setAttribute('iAlpha', iAlpha);

  // Nothing has been emitted yet, so there is nothing to draw. Grows as the pool
  // fills and stops at POOL once it has wrapped — until then, a fresh scene is
  // not paying to rasterise a thousand dead quads.
  geometry.instanceCount = 0;

  /*
   * Additive and unlit.
   *
   * Exhaust is a light source, not a surface — it should brighten whatever it is
   * in front of rather than being shaded by the star. Depth writing is off for
   * the same reason: a plume that occluded the hull it comes out of would punch
   * a hole in the ship.
   */
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: /* glsl */ `
      // <common> must come first: the log-depth chunks call isPerspectiveMatrix,
      // which is defined there. Without it the program fails to link and nothing
      // draws at all — no error, just no plume.
      #include <common>
      #include <logdepthbuf_pars_vertex>

      attribute vec3 iCentre;
      attribute vec3 iAxis;
      attribute vec2 iSize;
      attribute vec3 iColor;
      attribute float iAlpha;
      attribute float iRoll;

      varying vec3 vColor;
      varying float vAlpha;

      void main() {
        vColor = iColor;
        vAlpha = iAlpha;

        vec4 centre = modelViewMatrix * vec4(iCentre, 1.0);

        /*
         * The exhaust direction, as it lies on screen.
         *
         * ── Normalised *before* the roll, and that is the whole of it ────────
         *
         * The first version rolled the view-space axis and then used it, which
         * works from the side and does nothing at all from directly behind —
         * which is the view you are in almost all of the time, looking straight
         * up your own exhaust. Head-on the axis points at the camera, so its
         * screen projection is nearly zero-length, and rotating a vector of
         * length zero gives a vector of length zero. Every quad came out with
         * the same orientation and the roll appeared not to apply.
         *
         * Normalising first is what fixes it: the roll then turns a *direction*
         * rather than a magnitude, so it applies identically from every angle.
         * When there is no screen direction to speak of the fallback is fixed
         * and the roll supplies the whole angle, which is exactly right — a
         * plume pointing at you has no on-screen direction to fan around.
         */
        vec3 axis = (modelViewMatrix * vec4(iAxis, 0.0)).xyz;
        float len = length(axis.xy);
        vec2 facing = len > 1e-4 ? axis.xy / len : vec2(1.0, 0.0);

        float c = cos(iRoll);
        float s = sin(iRoll);
        vec2 along = vec2(facing.x * c - facing.y * s, facing.x * s + facing.y * c);
        vec2 across = vec2(-along.y, along.x);

        /*
         * Laid out on the screen plane, at the particle's own depth.
         *
         * Deliberately *not* foreshortened along the exhaust: a streak keeps its
         * full length whichever way it is pointing. Foreshortening is honest for
         * a streak that lies along the exhaust, and this one does not — it is
         * rolled off it by its own angle — so shortening it by how much of the
         * exhaust axis faces the camera would collapse the whole plume to a
         * scatter of dots in the one view that matters most.
         *
         * Distance still shrinks it, because these are view-space offsets and
         * the perspective divide has not happened yet.
         */
        centre.xy += position.x * iSize.x * along + position.y * iSize.y * across;

        gl_Position = projectionMatrix * centre;
        #include <logdepthbuf_vertex>
      }
    `,
    fragmentShader: /* glsl */ `
      precision mediump float;

      #include <common>
      #include <logdepthbuf_pars_fragment>

      varying vec3 vColor;
      varying float vAlpha;

      void main() {
        // Dead, or faded out. Cheaper to leave the pool at full size and reject
        // here than to keep the instance count in step with which slots are live.
        if (vAlpha <= 0.0) discard;

        #include <logdepthbuf_fragment>

        // Flat to its edges. A quad is a quad — the softening that was here made
        // it a sprite, which is the one thing everything else on screen is not.
        gl_FragColor = vec4(vColor, vAlpha);
      }
    `,
  });

  const mesh = new THREE.Mesh(geometry, material);
  // Always drawn. The bounding sphere describes the base quad, not where any
  // particle got to, so culling against it would blink the plume out.
  mesh.frustumCulled = false;
  /*
   * After the hull, so it adds over what is already there. It still *tests*
   * depth — a plume behind a station is behind it.
   *
   * Above the crossing shell at 8, which is the reason for the number. The shell
   * erases the framebuffer down to the gas layer, and it spares the hull only
   * because the hull is opaque and wrote depth — a plume writes none, so anything
   * drawn before the shell is erased along with the system. That took the plume
   * out for the whole of a crossing, which is exactly when `TUNNEL_FLAME` is
   * supposed to be the thing telling you where you are.
   *
   * Free to move: this is additive and depth-*tested*, so it still goes behind a
   * station it is behind, and additive blending does not care what order it
   * arrives in.
   */
  mesh.renderOrder = 9;
  scene.add(mesh);

  /** Per-particle state, in flat arrays for the same reason as the buffers. */
  const life = new Float32Array(POOL);
  const velocity = new Float32Array(POOL * 3);
  /** Absolute position. The scene's rebase is applied at draw time, not here. */
  const at = new Float64Array(POOL * 3);

  /** Where the next particle goes. Wraps, overwriting the oldest. */
  let next = 0;
  /** How many slots have ever been used, until the pool has filled once. */
  let used = 0;

  let hot = new THREE.Color(colors.hot);
  let cool = new THREE.Color(colors.cool);
  const tint = new THREE.Color();

  return {
    setColors: (chosen) => {
      hot = new THREE.Color(chosen.hot);
      cool = new THREE.Color(chosen.cool);
    },

    emit: (from, back, drift, strength, seconds, scale = 1, exhaust = EXHAUST_SPEED) => {
      if (strength <= 0) return;

      /*
       * How many to emit, rounded *stochastically*.
       *
       * ── Why the obvious version produced a pattern ──────────────────────────
       *
       * This used to carry the fraction over in a counter, which is the standard
       * trick for a rate that is not a whole number per frame. Two things went
       * wrong with it, and both show up as a plume that repeats rather than one
       * that sprays.
       *
       * The counter was shared by every nozzle, so the leftover from one thruster
       * decided how many the *next* one got. With a fixed frame rate that hands
       * the spare particle round the nozzles in a fixed cycle — the same thruster
       * gets the extra one every N frames, forever.
       *
       * And at sixty frames a second the rate divides exactly: `120 × 1/60` is
       * two, every frame, with no remainder at all. Perfectly regular emission
       * from perfectly regular positions, so however random each particle's
       * length, roll and colour are, they arrive in a repeating comb and the eye
       * reads the structure rather than the variation.
       *
       * Rounding at random instead emits `floor` or `ceil` with the right
       * probability, so the long-run rate is exactly `EMIT_RATE` and no two
       * frames are alike. No shared state, so nozzles stop being coupled to each
       * other — which is what made the repeat *visible*, since two thrusters
       * taking turns is a pattern you can watch.
       */
      const count = Math.floor(strength * EMIT_RATE * seconds + Math.random());

      for (let i = 0; i < count; i++) {
        const slot = next;
        next = (next + 1) % POOL;
        if (used < POOL) used++;

        life[slot] = LIFETIME;

        /*
         * Born somewhere along the path the nozzle swept, not all at the point
         * it reached.
         *
         * A frame is 16ms and a hull at cruise covers two units in one, so a
         * frame's worth of particles all spawned at the same place is a plume
         * that arrives in clumps. Backdating each one by a random fraction of
         * the frame lays them along the line the nozzle actually travelled,
         * which is where they would have come out. This is the only thing the
         * ship's velocity is used for.
         */
        const back_ = Math.random() * seconds;

        at[slot * 3] = from.x - drift.x * back_ + (Math.random() - 0.5) * MOUTH * scale;
        at[slot * 3 + 1] = from.y - drift.y * back_ + (Math.random() - 0.5) * MOUTH * scale;
        at[slot * 3 + 2] = from.z - drift.z * back_ + (Math.random() - 0.5) * MOUTH * scale;

        /*
         * Barely moving, and not carrying the ship's motion.
         *
         * A little way out of the nozzle and a little sideways, and that is all.
         * Everything that makes the plume look fast happens because the ship is
         * leaving it behind — see the note at the top.
         */
        velocity[slot * 3] = back.x * exhaust * scale + (Math.random() - 0.5) * SPREAD * scale;
        velocity[slot * 3 + 1] = back.y * exhaust * scale + (Math.random() - 0.5) * SPREAD * scale;
        velocity[slot * 3 + 2] = back.z * exhaust * scale + (Math.random() - 0.5) * SPREAD * scale;

        // Which way it lies, for as long as it lives.
        axes[slot * 3] = back.x;
        axes[slot * 3 + 1] = back.y;
        axes[slot * 3 + 2] = back.z;

        // Its size, for as long as it lives.
        sizes[slot * 2] =
          STREAK_LENGTH * 0.5 * scale * (1 + (Math.random() - 0.5) * 2 * LENGTH_JITTER);
        sizes[slot * 2 + 1] = STREAK_WIDTH * 0.5 * scale;

        // Its angle across the screen, for as long as it lives.
        rolls[slot] = (Math.random() - 0.5) * 2 * ROLL_SPREAD;

        // Its colour, for as long as it lives: one flat sample from anywhere
        // along the gradient.
        tint.copy(hot).lerp(cool, Math.random());
        tints[slot * 3] = tint.r;
        tints[slot * 3 + 1] = tint.g;
        tints[slot * 3 + 2] = tint.b;
      }

      if (count > 0) {
        iAxis.needsUpdate = true;
        iSize.needsUpdate = true;
        iColor.needsUpdate = true;
        iRoll.needsUpdate = true;
      }
    },

    step: (seconds, origin) => {
      geometry.instanceCount = used;

      for (let slot = 0; slot < used; slot++) {
        if (life[slot]! <= 0) {
          alphas[slot] = 0;
          continue;
        }

        const left = life[slot]! - seconds;
        life[slot] = left;
        if (left <= 0) {
          alphas[slot] = 0;
          continue;
        }

        const ax = at[slot * 3]! + velocity[slot * 3]! * seconds;
        const ay = at[slot * 3 + 1]! + velocity[slot * 3 + 1]! * seconds;
        const az = at[slot * 3 + 2]! + velocity[slot * 3 + 2]! * seconds;

        at[slot * 3] = ax;
        at[slot * 3 + 1] = ay;
        at[slot * 3 + 2] = az;

        // The one conversion, and the only place it happens: absolute in,
        // scene-space out.
        centres[slot * 3] = ax - origin.x;
        centres[slot * 3 + 1] = ay - origin.y;
        centres[slot * 3 + 2] = az - origin.z;

        // Bright at the nozzle, gone by the end. Nothing else about the particle
        // moves — not its colour, not its size, not its heading.
        alphas[slot] = left / LIFETIME;
      }

      iCentre.needsUpdate = true;
      iAlpha.needsUpdate = true;
    },

    dispose: () => {
      scene.remove(mesh);
      geometry.dispose();
      material.dispose();
    },
  };
}
